// @/services/app/store/index.js
import { BaseStore } from './base';
import { ValueListHandler } from './value-lists';
import { EmissionsHandler } from './modules/ca-emissions';
import { ActivitiesHandler } from './modules/ca-activities';
import { SpendHandler } from './modules/ca-spend';
import { SpendDistributionHandler } from './modules/ca-spend-distribution';
import { SupplierInputHandler } from './modules/ca-supplier-input';
import { CarbonYearHandler } from './modules/ca-year';
import { CompanyQuestionsHandler } from './modules/company-questions';
import { BusinessSegmentsHandler } from './modules/business-segments';
import { CustomerRequestsHandler } from './modules/customer-requests';
import { SupplychainSuppliersHandler } from './modules/supplychain-suppliers';
import { SupplierEngagementBatchInviteHandler } from './modules/supplier-engagement-batch-invite';
import { SupplierEngagementBatchRemindHandler } from './modules/supplier-engagement-batch-remind';
import { ItemsSuppliersHandler } from './modules/items-suppliers';
import { CompanyProfileHandler } from './modules/company-profile';
import { UsersHandler } from './modules/users';
import { WebSocketHandler } from '../websocket';
import { DataPopulationHandler } from './data-population';
import { authService } from '@/services/auth';
import { clearRedirectLoopCounter } from '@/utils/session-utils';
import { scmPermissionsService } from '@/services/scm-permissions';
import { permissionsService } from '@/services/permissions/permissions';
import { privilegesService } from '@/services/permissions/privilegesService';
import { AnnouncementsHandler } from './modules/announcements';
import { TermsAcceptanceHandler } from './modules/terms-acceptance';

/**
 * AppDataStore
 * 
 * Application-specific store extending BaseStore with:
 * - Module handler initialization and orchestration
 * - Permission services integration
 * - Performance-optimized loading strategies
 * - Route-aware data hydration
 * - WebSocket integration for real-time updates
 * - Data population coordination
 */
class AppDataStore extends BaseStore {
  /**
   * Initialize the application data store with all module handlers
   */
  constructor() {
    super();

    // Set auth service reference
    this.authService = authService;

    // Initialize handlers in dependency order
    this.valueListHandler = new ValueListHandler(this);
    this.companyProfileHandler = new CompanyProfileHandler(this);

    // Self-assessment handlers
    this.customerRequestsHandler = new CustomerRequestsHandler(this);
    this.businessSegmentsHandler = new BusinessSegmentsHandler(this);
    this.companyQuestionsHandler = new CompanyQuestionsHandler(this);

    // Supply chain handlers
    this.supplychainSuppliersHandler = new SupplychainSuppliersHandler(this);
    this.itemsSuppliersHandler = new ItemsSuppliersHandler(this);
    this.supplierEngagementBatchInviteHandler = new SupplierEngagementBatchInviteHandler(this);
    this.supplierEngagementBatchRemindHandler = new SupplierEngagementBatchRemindHandler(this);

    // User management handler
    this.usersHandler = new UsersHandler(this);

    // Carbon accounting handlers
    this.emissionsHandler = new EmissionsHandler(this);
    this.activitiesHandler = new ActivitiesHandler(this);
    this.carbonYearHandler = new CarbonYearHandler(this);
    this.spendHandler = new SpendHandler(this);
    this.spendDistributionHandler = new SpendDistributionHandler(this);
    this.supplierInputHandler = new SupplierInputHandler(this);

    // Data population and WebSocket handlers
    this.dataPopulationHandler = new DataPopulationHandler(this);
    this.announcementsHandler = new AnnouncementsHandler(this);
    this.termsAcceptanceHandler = new TermsAcceptanceHandler(this);
    this.webSocketHandler = new WebSocketHandler(this);

    // Make available to global scope for component access
    if (typeof window !== 'undefined') {
      window.appStore = this;
    }

    // App-specific state
    this.moduleLoadStatus = {
      critical: false,
      nonCritical: false
    };

    // Performance tracking
    this.performanceMetrics = {
      startTime: 0,
      steps: new Map()
    };

    // Define module priority groups for optimized loading
    // Phase 1 (identity): MUST complete before any routing decision
    // Phase 2 (secondary init): Permission services, value lists
    // Phase 3 (critical): Needed for overview/dashboard
    // Phase 4 (parallel): Suppliers (first 100) + CA modules run in parallel
    // Phase 5 (background): Users, questions, remaining suppliers (chunked)
    this.moduleGroups = {
      identity: ['companyProfile', 'privileges'],  // ✅ MUST load first - determines firstRun/routing
      critical: ['customerRequests', 'businessSegments'],
      parallel: ['supplychainSuppliers', 'carbonAccounting'],  // ✅ Run in parallel after critical
      background: ['users', 'companyQuestions']  // Sequential after parallel phase
    };

    // Track initialization promise to prevent duplicate calls
    this._initializationPromise = null;

    // Hydration deduplication - prevents duplicate API calls
    this._lastHydration = { path: null, timestamp: 0 };
    this._hydrationCooldownMs = 3000; // 3 seconds cooldown after init
  }

  /**
   * Initialize the application store with all required data
   * @returns {Promise<boolean>} Success status of initialization
   */
  async initialize() {
    // Return existing promise if initialization is already in progress
    if (this._initializationPromise) {
      return this._initializationPromise;
    }

    this._initializationPromise = this._performInitialization();

    try {
      const result = await this._initializationPromise;
      return result;
    } finally {
      // Clear the promise after completion
      this._initializationPromise = null;
    }
  }

  /**
   * Core initialization logic with optimized module loading
   * @private
   * @returns {Promise<boolean>} Success status
   */
  async _performInitialization() {
    this.recordPerformance('start');

    try {
      // CRITICAL FIX: Skip initialization for public pages and token pages
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const params = new URLSearchParams(window.location.search);
        const hasToken = params.has('token') || params.has('invitation') || params.has('invitationToken');

        // Skip full initialization for public pages or token pages
        if (currentPath === '/register' || currentPath === '/register/' || hasToken) {
          // Still initialize basic stores for UI functionality
          await this.valueListHandler.initialize();

          // Mark as initialized but skip auth and data loading
          this.state.initialized = true;
          this.sessionInfo.initialized = true;
          this.recordPerformance('complete_public_page');

          return true;
        }
      }

      // Use base initialization first
      const { fastPath, restored } = await this.initializeBase();

      if (fastPath && restored) {
        this.recordPerformance('cache_restored');

        const projectId = this.getProjectId();

        // ✅ CRITICAL: Even on fast-path, refresh identity data to ensure
        // profile and privileges are current (prevents stale firstRun status)
        if (projectId) {
          // Refresh identity data in parallel (awaited, not background).
          // allSettled — a heavy companyProfile failure (timeout, partial 5xx)
          // must not cancel an already-resolving privileges fetch. Either side
          // failing on its own is non-fatal here; the user may render with a
          // stale value briefly and the subscriber pattern in usePermissions
          // will catch up when the slower side completes.
          const [profileSettled, privilegesSettled] = await Promise.allSettled([
            this.companyProfileHandler.loadProfileData(true),
            privilegesService.getPrivileges(true)
          ]);
          if (profileSettled.status === 'rejected') {
            this.handleError('companyProfileFastPath', profileSettled.reason);
          }
          if (privilegesSettled.status === 'rejected') {
            this.handleError('privilegesFastPath', privilegesSettled.reason);
          }

          this.recordPerformance('identity_refreshed');
        }

        // Initialize other permission services
        await this._initializePermissions();
        this.recordPerformance('permissions_initialized');

        // Initialize WebSocket (even on fast path — must reconnect after page refresh)
        this._initializeWebSocketAsync();

        // Start background refresh of other critical modules (non-blocking)
        this._refreshCriticalModulesInBackground();

        this.recordPerformance('complete_fast_path');
        this.logPerformance();

        return true;
      }

      // Full initialization path

      // Reset redirect loop counter
      clearRedirectLoopCounter();

      // Check authentication
      this.recordPerformance('auth_check_start');
      const isAuthenticated = await this._checkAuthentication();
      this.recordPerformance('auth_check_complete');

      if (!isAuthenticated) {
        // Store state to prevent unnecessary redirects
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('loginAttempted', 'true');
          sessionStorage.setItem('loginAttemptTimestamp', Date.now().toString());
        }

        await authService.login();
        return false;
      }

      // Get project ID
      const projectId = this.getProjectId();
      if (!projectId) {
        throw new Error("Missing project ID after authentication");
      }

      // ============================================================
      // PHASE 1: IDENTITY DATA (BLOCKING)
      // Profile + Privileges MUST load completely before ANY routing
      // This prevents firstRun/privilege issues with heavy accounts
      // ============================================================
      await this._loadIdentityData(projectId);
      this.recordPerformance('identity_data_loaded');

      // ============================================================
      // PHASE 2: SECONDARY INITIALIZATION (parallel, non-blocking for routing)
      // Value lists and permission services
      // ============================================================
      const secondaryInitPromises = [
        this._initializePermissions(),  // SCM permissions, etc (uses already-loaded privileges)
        this.valueListHandler.initialize()
      ];

      await Promise.all(secondaryInitPromises);
      this.recordPerformance('handlers_initialized');

      // ============================================================
      // PHASE 3: CRITICAL DATA
      // Data needed for overview/dashboard
      // ============================================================
      await this._loadCriticalData(projectId);
      this.moduleLoadStatus.critical = true;
      this.recordPerformance('critical_data_loaded');

      // Initialize WebSocket in background
      this._initializeWebSocketAsync();

      // ✅ FIX: Load all remaining data sequentially at login (not in background)
      await this._loadAllDataSequentially(projectId);

      // Signal DataPopulationHandler that initialization is complete
      this.dataPopulationHandler.markInitializationComplete();

      // ============================================================
      // OPERATION LIGHTSABER: Check if Strapi was empty (first login)
      // If core modules are empty, BLOCK until population completes
      // This ensures users see data immediately, not a blank screen
      // ============================================================
      const isStrapiEmpty = this._isCoreDataEmpty();

      if (isStrapiEmpty) {
        console.log('[AppStore] Strapi empty for core modules - blocking until population completes');
        this.recordPerformance('strapi_empty_detected');

        // AWAIT population - this blocks until Snowflake data is loaded
        await this.dataPopulationHandler.checkAndPopulateMissingData(projectId, { force: true });
        this.recordPerformance('population_complete');
      }

      // Mark as initialized AFTER population is complete (if needed)
      this.state.initialized = true;
      this.sessionInfo.initialized = true;
      this.sessionInfo.lastFullInit = new Date().toISOString();

      // Save state
      await this.saveState();
      this.recordPerformance('state_saved');

      // For returning users (Strapi had data), check population
      if (!isStrapiEmpty) {
        // Check if Snowflake flagged that repopulation is needed
        const needsRepopulation = privilegesService.needsRepopulation();

        if (needsRepopulation) {
          const modulesToRepopulate = privilegesService.getModulesNeedingRepopulation();
          console.log('[AppStore] Snowflake flagged repopulation needed for modules:', modulesToRepopulate);
          
          // BLOCKING: Force repopulation from Snowflake for flagged modules
          const repopulationResult = await this.dataPopulationHandler.repopulateAfterAction(
            modulesToRepopulate,
            true,
            { force: true, source: 'snowflake-flag' }
          );

          // Clear flags in Snowflake ONLY for modules that actually
          // repopulated — a failed or no-op populate must leave its flag so
          // the next login retries. Clearing unconditionally here is what
          // made flags vanish while stale data survived.
          const succeededModules = (repopulationResult?.results || [])
            .filter(moduleResult => moduleResult.success)
            .map(moduleResult => moduleResult.dataType);
          const snowflakeModulesToClear = privilegesService.getClearableSnowflakeModules(succeededModules);

          // Clear the local flags (re-derived from Snowflake on next fetch,
          // so uncleared Snowflake flags re-trigger repopulation next login)
          privilegesService.clearRepopulationFlag();

          if (snowflakeModulesToClear.length > 0) {
            // Fire and forget - don't block on this
            privilegesService.clearRepopulationFlagsInSnowflake(snowflakeModulesToClear).catch(err => {
              console.warn('[AppStore] Failed to clear Snowflake flags:', err);
            });
          } else {
            console.warn('[AppStore] Repopulation did not fully succeed — leaving Snowflake flags in place', repopulationResult?.results);
          }
          
          this.recordPerformance('repopulation_complete');
        } else {
          this.dataPopulationHandler.checkAndPopulateMissingData(projectId);
        }
      }

      this.recordPerformance('complete');
      this.logPerformance();

      return true;
    } catch (error) {
      this.handleError('initialization', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('[AppStore] Initialization failed:', error);
      }
      return false;
    }
  }

  /**
   * Check authentication status
   * @private
   * @returns {Promise<boolean>} Whether user is authenticated
   */
  async _checkAuthentication() {
    // For reloads, use cached auth state if available
    if (this.sessionInfo.isReload) {
      const authState = authService.getAuthState();
      if (authState?.authenticated && authState?.tokenExpiry > Date.now()) {
        return true;
      }
    }

    // Full authentication check
    return authService.initialize();
  }

  /**
   * Load identity data: company profile and privileges
   * This MUST complete before any routing decisions
   *
   * @private
   * @param {string|number} projectId - The project ID
   * @returns {Promise<boolean>} Success status
   */
  async _loadIdentityData(_projectId) {
    try {
      // Load profile and privileges in parallel, AWAIT both, but isolate
      // failures. allSettled — a heavy companyProfile failure (heavy account,
      // timeout, partial 5xx) must NOT cancel an already-resolving privileges
      // fetch. Previously `Promise.all` would short-circuit on the first
      // rejection, leaving privileges in an inconsistent state for the rest of
      // the session (TÜV SÜD enterprise-CA-invisible bug, #882).
      const [profileSettled, privilegesSettled] = await Promise.allSettled([
        this.companyProfileHandler.loadProfileData(true),  // Force fresh load
        privilegesService.getPrivileges(true)              // Force fresh load
      ]);

      const profileResult = profileSettled.status === 'fulfilled' ? profileSettled.value : null;
      const privilegesResult = privilegesSettled.status === 'fulfilled' ? privilegesSettled.value : null;

      if (profileSettled.status === 'rejected') {
        this.handleError('companyProfileIdentity', profileSettled.reason);
        console.warn('[AppStore] Profile load failed during identity phase — routing may be affected', profileSettled.reason);
      }
      if (privilegesSettled.status === 'rejected') {
        this.handleError('privilegesIdentity', privilegesSettled.reason);
        console.warn('[AppStore] Privileges load failed during identity phase — using cached/default', privilegesSettled.reason);
      }

      // Validate that we got the data
      const profileLoaded = !!profileResult || !!this.state.data.companyProfile;
      const privilegesLoaded = !!privilegesResult;

      // Profile is critical - if it fails, we might have routing issues
      if (!profileLoaded) {
        console.warn('[AppStore] Profile data not loaded - routing may be affected');
      }

      return profileLoaded && privilegesLoaded;
    } catch (error) {
      this.handleError('identityData', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('[AppStore] Failed to load identity data:', error);
      }
      // Don't throw - let initialization continue with defaults
      return false;
    }
  }

  /**
   * Initialize all permission services
   * @private
   * @returns {Promise<void>}
   */
  async _initializePermissions() {
    try {
      await Promise.all([
        privilegesService.getPrivileges(),
        permissionsService.initialize(),
        scmPermissionsService.initialize()
      ]);
    } catch (error) {
      // Log but don't fail initialization
      this.handleError('permissions', error);
      if (process.env.NODE_ENV === 'development') {
        console.warn('[AppStore] Permissions initialization error (non-fatal):', error);
      }
    }
  }

  /**
   * Initialize WebSocket asynchronously
   * @private
   */
  async _initializeWebSocketAsync() {
    setTimeout(async () => {
      try {
        await this.webSocketHandler.initialize();
        this.supplierEngagementBatchInviteHandler?.registerWebSocketHandlers();
        this.supplierEngagementBatchRemindHandler?.registerWebSocketHandlers();
        this.recordPerformance('websocket_initialized');
      } catch (error) {
        this.handleError('webSocket', error);
      }
    }, 100);
  }

  /**
   * Load critical data with parallel loading where possible
   * NOTE: companyProfile is now loaded in Phase 1 (_loadIdentityData)
   *
   * @private
   * @param {string|number} projectId - The project ID
   * @returns {Promise<boolean>} Success status
   */
  async _loadCriticalData(_projectId) {
    try {
      // ✅ companyProfile removed - already loaded in Phase 1 (identity data)
      const loadPromises = [
        this.customerRequestsHandler.loadCustomerRequests(),
        this.businessSegmentsHandler.loadBusinessSegments(false) // Operation Lightsaber: GET from Strapi, no Snowflake
      ];

      const results = await Promise.allSettled(loadPromises);

      // Check if all succeeded
      const allSucceeded = results.every(result => result.status === 'fulfilled');

      if (allSucceeded) {
        this.recordPerformance('parallel_critical_loaded');
        return true;
      }

      // Fallback to sequential loading for failed modules
      const moduleNames = ['customerRequests', 'businessSegments'];
      for (let i = 0; i < results.length; i++) {
        if (results[i].status === 'rejected') {
          const moduleName = moduleNames[i];
          this.handleError(moduleName, results[i].reason);

          // Retry once
          try {
            if (i === 0) await this.customerRequestsHandler.loadCustomerRequests();
            else if (i === 1) await this.businessSegmentsHandler.loadBusinessSegments(false);

            this.recordPerformance(`${moduleName}_retry_success`);
          } catch (retryError) {
            // Continue with other modules even if one fails
            if (process.env.NODE_ENV === 'development') {
              console.warn(`[AppStore] Failed to load ${moduleName} after retry:`, retryError);
            }
          }
        }
      }

      return true;
    } catch (error) {
      this.handleError('criticalData', error);
      return false;
    }
  }

  /**
   * Load remaining data after critical phase
   *
   * Strategy (Operation Lightsaber):
   * - First 100 suppliers load IN PARALLEL with CA modules
   * - This ensures SCM users get fast initial render while CA loads
   * - CA modules load sequentially (they have dependencies)
   * - Users/questions load after both complete
   * - Remaining suppliers (100+) load in background via chunked loading
   *
   * @private
   * @param {string|number} projectId - The project ID
   * @returns {Promise<boolean>} Success status
   */
  async _loadAllDataSequentially(_projectId) {
    try {
      // ============================================================
      // PARALLEL LOADING: First 100 suppliers + CA modules
      // SCM users get immediate data while CA loads in parallel
      // ============================================================
      await Promise.allSettled([
        // Suppliers: loads first 100, remaining chunk in background automatically
        this._loadSuppliersInitialBatch(),
        // CA modules: load sequentially (they depend on each other)
        this._loadCarbonAccountingModules()
      ]);

      this.recordPerformance('parallel_phase4_loaded');

      // ============================================================
      // SEQUENTIAL: Users and questions (less critical)
      // ============================================================
      try {
        await this.usersHandler.loadUsers();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[AppStore] Failed to load users:', error);
        }
      }

      try {
        await this.companyQuestionsHandler.loadCompanyQuestions();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[AppStore] Failed to load questions:', error);
        }
      }

      this.recordPerformance('all_data_loaded');
      this.moduleLoadStatus.nonCritical = true;

      // Save updated state
      await this.saveState();

      return true;
    } catch (error) {
      this.handleError('sequentialLoading', error);
      return false;
    }
  }

  /**
   * Load first 100 suppliers for immediate SCM render
   * Remaining suppliers load in background via chunked loading
   * @private
   * @returns {Promise<Array>} First batch of suppliers
   */
  async _loadSuppliersInitialBatch() {
    try {
      // This triggers the chunked loading - first 100 return immediately,
      // remaining load in background automatically
      const result = await this.supplychainSuppliersHandler.loadSupplychainSuppliers();
      this.recordPerformance('suppliers_initial_loaded');
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[AppStore] Failed to load suppliers:', error);
      }
      return [];
    }
  }

  /**
   * Load Carbon Accounting modules with optimized parallelization
   *
   * Strategy:
   * - emissions, activities, spendVolume, spendDistribution load in PARALLEL
   * - supplierInput loads AFTER spendDistribution (has dependency on it)
   *
   * NOTE: Lead firms (GRANTED_ITEMS) skip loading traditional CA modules.
   * They use dedicated item+s API endpoints instead.
   *
   * @private
   * @returns {Promise<boolean>} Success status
   */
  async _loadCarbonAccountingModules() {
    // Skip CA modules for lead firms - they use dedicated item+s API endpoints
    if (privilegesService.isLeadFirm()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AppStore] Skipping traditional CA modules for lead firm (uses item+s API)');
      }
      this.recordPerformance('ca_modules_skipped_leadfirm');
      return true;
    }

    // Phase 1: Load independent modules in parallel
    const parallelModules = [
      { name: 'emissions', loader: () => this.emissionsHandler.loadAllEmissionResults() },
      { name: 'activities', loader: () => this.activitiesHandler.loadActivityData() },
      { name: 'spendVolume', loader: () => this.spendHandler.loadSpendVolume() },
      { name: 'spendDistribution', loader: () => this.spendDistributionHandler.loadSpendDistribution() }
    ];

    const results = await Promise.allSettled(
      parallelModules.map(module => module.loader())
    );

    // Log any failures
    results.forEach((result, index) => {
      if (result.status === 'rejected' && process.env.NODE_ENV === 'development') {
        console.warn(`[AppStore] Failed to load ${parallelModules[index].name}:`, result.reason);
      }
    });

    this.recordPerformance('ca_parallel_modules_loaded');

    // Phase 2: Load supplierInput (depends on spendDistribution being loaded)
    try {
      await this.supplierInputHandler.loadSupplierInput();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[AppStore] Failed to load supplierInput:', error);
      }
    }

    this.recordPerformance('ca_modules_loaded');
    return true;
  }

  /**
   * Check if core data modules are empty (Strapi returned no data)
   * Used to detect first-login / empty-Strapi scenarios
   * @private
   * @returns {boolean} True if core modules are empty
   */
  _isCoreDataEmpty() {
    // Check the three core modules that indicate if Strapi has data
    const customerRequestsEmpty = !this.state.data.customerRequests?.requests?.length;
    const businessSegmentsEmpty = !Object.keys(this.state.data.businessSegments?.bySector || {}).length ||
      !Object.values(this.state.data.businessSegments?.bySector || {}).some(arr => arr?.length > 0);
    const companyQuestionsEmpty = !this.state.data.companyQuestions?.data?.questionnaire?.length;

    // If ALL core modules are empty, Strapi has no data for this project
    const allEmpty = customerRequestsEmpty && businessSegmentsEmpty && companyQuestionsEmpty;

    if (process.env.NODE_ENV === 'development' && allEmpty) {
      console.log('[AppStore] Core data empty check:', {
        customerRequestsEmpty,
        businessSegmentsEmpty,
        companyQuestionsEmpty,
        result: 'STRAPI EMPTY - will block for population'
      });
    }

    return allEmpty;
  }

  /**
   * Refresh critical modules in background (only if stale)
   * @private
   */
  async _refreshCriticalModulesInBackground() {
    setTimeout(async () => {
      try {
        const projectId = this.getProjectId();
        if (!projectId) return;

        const FRESHNESS_MS = 30 * 1000; // 30 seconds - skip if just loaded
        const refreshPromises = [];

        // Only refresh customerRequests if stale (not just loaded)
        const crLastSync = this.state.data.customerRequests?.meta?.lastSync;
        const crIsStale = !crLastSync || (Date.now() - new Date(crLastSync).getTime() > FRESHNESS_MS);
        if (crIsStale) {
          refreshPromises.push(this.customerRequestsHandler.loadCustomerRequests(true));
        }

        // Permissions refresh (lightweight) — don't force, respects 5-min cache.
        // Store init already did getPrivileges(true), no need to force again immediately.
        refreshPromises.push(permissionsService.refreshPermissions());
        refreshPromises.push(scmPermissionsService.refreshPermissions());

        if (refreshPromises.length > 0) {
          await Promise.allSettled(refreshPromises);
          await this.saveState();
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[AppStore] Background refresh error:', error);
        }
      }
    }, 0);
  }

  /**
   * Hydrate data for a specific route (with deduplication)
   * @param {string} path - Current route path
   * @returns {Promise<boolean>} Success status
   */
  async hydrateForRoute(path) {
    if (!path || !this.state.initialized) return false;

    const now = Date.now();

    // DEDUPLICATION: Skip if same path within cooldown period
    if (this._lastHydration.path === path &&
        now - this._lastHydration.timestamp < this._hydrationCooldownMs) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Hydration] Skipped (duplicate within ${this._hydrationCooldownMs}ms): ${path}`);
      }
      return true;
    }

    // DEDUPLICATION: Skip if we JUST initialized (data is fresh)
    const timeSinceInit = now - (this.performanceMetrics.steps.get('complete') || 0) - this.performanceMetrics.startTime;
    if (timeSinceInit < this._hydrationCooldownMs && this.performanceMetrics.steps.has('complete')) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Hydration] Skipped (just initialized ${timeSinceInit}ms ago): ${path}`);
      }
      return true;
    }

    // Update last hydration tracking
    this._lastHydration = { path, timestamp: now };

    // Route-to-data mapping
    // NOTE: More specific routes MUST come before less specific ones (e.g., /carbon-accounting-items before /carbon-accounting)
    const routeDataMap = {
      '/overview': ['customerRequests', 'businessSegments', 'companyQuestions'],
      '/self-assessment': ['businessSegments', 'companyQuestions'],
      '/carbon-accounting-items': [], // item+s pages load their own data via dedicated API endpoints
      '/carbon-accounting': ['emissionResults', 'activityData', 'spendVolume', 'spendDistribution', 'supplierInput'],
      // Rebrand (#885): /supplier-engagement is the new canonical URL for the
      // Supplier Engagement (formerly Supply Chain Management) module. Both
      // paths hydrate the same store modules so deep-links to either URL work.
      '/supplier-engagement': ['supplychainSuppliers'],
      '/supply-chain-management': ['supplychainSuppliers'],
      '/account/company-profile': ['companyProfile', 'users'],
      '/account/users': ['users'],
      '/account/settings': ['users', 'companyProfile']
    };

    // Find modules for this route
    let modulesToRefresh = [];
    for (const [route, modules] of Object.entries(routeDataMap)) {
      if (path.startsWith(route)) {
        modulesToRefresh = modules;
        break;
      }
    }

    if (modulesToRefresh.length === 0) {
      return true;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Hydration] ${path} → ${modulesToRefresh.join(', ')}`);
    }

    // Perform hydration in background
    this._hydrateModulesAsync(modulesToRefresh);

    return true;
  }

  /**
   * Hydrate modules asynchronously (with freshness checks)
   * @private
   * @param {string[]} modules - Modules to hydrate
   */
  async _hydrateModulesAsync(modules) {
    setTimeout(async () => {
      const FRESHNESS_MS = 5 * 60 * 1000; // 5 minutes

      const handlerMap = {
        'customerRequests': () => {
          // Skip if data is recent (< 5 min old)
          const lastSync = this.state.data.customerRequests?.meta?.lastSync;
          const isRecent = lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS);
          if (isRecent) {
            return Promise.resolve();
          }
          return this.customerRequestsHandler.loadCustomerRequests();
        },
        'businessSegments': () => {
          if (this.state.loading.businessSegments) {
            return Promise.resolve();
          }

          // Check freshness — same pattern as customerRequests.
          // Previous code checked "has data" (any data at all), meaning sessionStorage
          // data was NEVER refreshed on CMD+R. After accept/decline, the stale
          // sessionStorage snapshot would persist until re-login.
          const lastSync = this.state.data.businessSegments?.meta?.lastSync;
          const isRecent = lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS);
          if (isRecent) {
            return Promise.resolve();
          }
          return this.businessSegmentsHandler.loadBusinessSegments();
        },
        'companyQuestions': () => {
          // Skip if data is recent (< 5 min old)
          const lastSync = this.state.data.companyQuestions?.meta?.lastSync;
          const isRecent = lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS);
          if (isRecent) {
            return Promise.resolve();
          }
          return this.companyQuestionsHandler.loadCompanyQuestions();
        },
        'supplychainSuppliers': () => {
          const isLoading = this.supplychainSuppliersHandler?.isLoading;

          if (isLoading) {
            return Promise.resolve();
          }

          // ✅ FIX: Check if data was FETCHED, not if list has items
          // This correctly treats "0 suppliers" as valid data (not missing data)
          const isStale = this.supplychainSuppliersHandler?.dataFreshness?.isStale;
          const hasFetched = this.supplychainSuppliersHandler?.lastFetchTime > 0;

          if (isStale || !hasFetched) {
            return this.supplychainSuppliersHandler.loadSupplychainSuppliers(); // No force
          }

          return Promise.resolve();
        },
        'emissionResults': () => {
          const lastSync = this.state.data.emissionResults?.meta?.lastSync;
          if (lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS)) {
            return Promise.resolve();
          }
          return this.emissionsHandler.loadAllEmissionResults();
        },
        'activityData': () => {
          const lastSync = this.state.data.activityData?.meta?.lastSync;
          if (lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS)) {
            return Promise.resolve();
          }
          return this.activitiesHandler.loadActivityData();
        },
        'spendVolume': () => {
          const lastSync = this.state.data.spendVolume?.meta?.lastSync;
          if (lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS)) {
            return Promise.resolve();
          }
          return this.spendHandler.loadSpendVolume();
        },
        'spendDistribution': () => {
          const lastSync = this.state.data.spendDistribution?.meta?.lastSync;
          if (lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS)) {
            return Promise.resolve();
          }
          return this.spendDistributionHandler.loadSpendDistribution();
        },
        'supplierInput': () => {
          const lastSync = this.state.data.supplierInput?.meta?.lastSync;
          if (lastSync && (Date.now() - new Date(lastSync).getTime() < FRESHNESS_MS)) {
            return Promise.resolve();
          }
          return this.supplierInputHandler.loadSupplierInput();
        },
        'companyProfile': () => this.companyProfileHandler.loadProfileData(), // ✅ No force
        'users': () => this.usersHandler.loadUsers() // ✅ No force
      };

      let hydratedCount = 0;

      for (const module of modules) {
        try {
          if (handlerMap[module]) {
            await handlerMap[module]();
            hydratedCount++;
          }
        } catch (error) {
          this.handleError(`hydrate_${module}`, error);
        }
      }

      if (hydratedCount > 0) {
        await this.saveState();
      }
    }, 0);
  }

  /**
   * Record performance metrics
   * @param {string} step - Step name
   */
  recordPerformance(step) {
    if (!this.performanceMetrics.startTime) {
      this.performanceMetrics.startTime = Date.now();
    }

    this.performanceMetrics.steps.set(step, Date.now() - this.performanceMetrics.startTime);
  }

  /**
   * Log performance metrics (simplified - only total time and errors)
   */
  logPerformance() {
    if (process.env.NODE_ENV !== 'development') return;

    const steps = this.performanceMetrics.steps;
    if (steps.size === 0) return;

    const totalTime = steps.get('complete') || steps.get('complete_fast_path') || '?';

    // Log any errors
    if (this.operationErrors.size > 0) {
      console.warn(`[AppStore] Init completed in ${totalTime}ms with ${this.operationErrors.size} errors:`,
        Array.from(this.operationErrors.keys())
      );
    }
    // Detailed step logging removed - enable VERBOSE_APPSTORE env var if needed
  }

  /**
   * Clear all application state including permission services
   * @override
   * @returns {Promise<boolean>} Success status
   */
  async clearState() {
    // Clear base state first
    await this.clearBaseState();

    // Clear permission services
    try {
      if (privilegesService) {
        privilegesService.clearPrivileges();
      }

      if (scmPermissionsService) {
        scmPermissionsService.clearPermissions();
      }

      if (permissionsService && permissionsService.clearPermissions) {
        permissionsService.clearPermissions();
      }
    } catch (error) {
      // Ignore errors during cleanup
      if (process.env.NODE_ENV === 'development') {
        console.warn('[AppStore] Error clearing permission services:', error);
      }
    }

    // Clear app-specific state
    this.moduleLoadStatus = {
      critical: false,
      nonCritical: false
    };

    // Clear performance metrics
    this.performanceMetrics = {
      startTime: 0,
      steps: new Map()
    };

    // Reset handler instance properties to prevent state leakage between accounts
    try {
      if (this.customerRequestsHandler) {
        this.customerRequestsHandler.isLoading = false;
        this.customerRequestsHandler.globalLastSubmitDate = null;
        this.customerRequestsHandler.submissionCooldownUntil = 0;
        if (this.customerRequestsHandler.abortControllers) {
          this.customerRequestsHandler.abortControllers.forEach(c => { try { c.abort(); } catch (e) { /* */ } });
          this.customerRequestsHandler.abortControllers = new Map();
        }
      }

      if (this.businessSegmentsHandler) {
        this.businessSegmentsHandler.isLoading = false;
      }

      if (this.supplychainSuppliersHandler) {
        this.supplychainSuppliersHandler.isLoading = false;
        this.supplychainSuppliersHandler._loadingPromise = null;
        this.supplychainSuppliersHandler._isStale = false;
      }

      if (this.dataPopulationHandler) {
        this.dataPopulationHandler.populationInProgress.clear();
        this.dataPopulationHandler.populationResults.clear();
        this.dataPopulationHandler.projectCheckTimestamps.clear();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[AppStore] Error resetting handler state:', error);
      }
    }

    // Clear session storage items managed by app store
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('scmPermissions');
        sessionStorage.removeItem('projectPrivileges');
      } catch (e) {
        // Ignore storage errors
      }
    }

    return true;
  }

  /**
   * Check if module data needs refresh
   * @param {string} moduleName - Module name to check
   * @param {number} freshnessThreshold - Freshness threshold in milliseconds (default: 30 minutes)
   * @returns {boolean} True if refresh needed
   */
  needsRefresh(moduleName, freshnessThreshold = 30 * 60 * 1000) {
    // Always refresh if module data not present
    if (!this.state.data[moduleName]) return true;

    // Check if module has meta.lastSync field
    const lastSync = this.state.data[moduleName]?.meta?.lastSync;
    if (!lastSync) return true;

    // Check freshness
    const timeSinceSync = Date.now() - new Date(lastSync).getTime();
    return timeSinceSync > freshnessThreshold;
  }

  /**
   * Get module loading priority
   * @param {string} moduleName - Module name to check
   * @returns {string} Priority category (critical, secondary, background, or unknown)
   */
  getModulePriority(moduleName) {
    if (this.moduleGroups.critical.includes(moduleName)) {
      return 'critical';
    }

    if (this.moduleGroups.secondary.includes(moduleName)) {
      return 'secondary';
    }

    return this.moduleGroups.background.includes(moduleName) ? 'background' : 'unknown';
  }
}

// Export singleton instance
export const appStore = new AppDataStore();